interface UploadFormData {
  username: string;
  description: string;
  file: File | null;
  turnstileToken: string;
}

export default class UploadModal {
  private modalContainer: HTMLElement | null = null;
  private formData: UploadFormData = {
    username: '',
    description: '',
    file: null,
    turnstileToken: ''
  };
  private onUploadSuccess?: (imageData: any) => void;
  private turnstileWidgetId: string | null = null;

  constructor(onUploadSuccess?: (imageData: any) => void) {
    this.onUploadSuccess = onUploadSuccess;
    this.createModal();
  }

  createModal() {
    this.modalContainer = document.createElement('div');
    this.modalContainer.id = 'upload-modal';
    this.modalContainer.className = 'modal-hidden';
    
    this.modalContainer.innerHTML = `
      <div class="modal-overlay"></div>
      <div class="modal-content upload-modal-content">
        <button class="modal-close">&times;</button>
        <h2 class="upload-title">Post your Cool S</h2>
        
        <div class="upload-modal-body">
          <div class="upload-form-container">
            <form id="upload-form" class="upload-form">
              <div class="upload-form-fields">
                <div class="form-group">
                  <label for="username">Your Name</label>
                  <input type="text" id="username" name="username" required placeholder="Enter your name" />
                </div>
                
                <div class="form-group">
                  <label for="description">Description</label>
                  <textarea id="description" name="description" rows="3" placeholder="Tell us about your Cool S..."></textarea>
                </div>
                
                <div class="form-group">
                  <label for="file-input">Upload Image</label>
                  <input type="file" id="file-input" name="file" accept="image/*" required />
                </div>
              </div>
              <!-- Preview appears here in mobile, to the right on desktop -->
              <div class="image-preview-container" id="preview-container">
                <img id="image-preview" class="image-preview" src="" alt="Preview" />
              </div>
              <div class="upload-form-fields">
              <div class="captcha-container" id="captcha-container"></div>
              
              
              <button type="submit" class="submit-button">Upload Cool S</button>
              </div>
            </form>
          </div>
        </div>
        
        <div class="loading-overlay" style="display: none;">
          <div class="loading-spinner"></div>
          <p class="loading-text">Uploading your Cool S...</p>
        </div>
      </div>
    `;

    document.body.appendChild(this.modalContainer);

    // Event listeners
    const closeBtn = this.modalContainer.querySelector('.modal-close');
    const overlay = this.modalContainer.querySelector('.modal-overlay');
    const form = this.modalContainer.querySelector('#upload-form') as HTMLFormElement;
    const fileInput = this.modalContainer.querySelector('#file-input') as HTMLInputElement;
    
    closeBtn?.addEventListener('click', () => this.close());
    overlay?.addEventListener('click', () => this.close());
    form?.addEventListener('submit', (e) => this.handleSubmit(e));
    fileInput?.addEventListener('change', () => this.handleFileSelect());
  }

  open() {
    if (!this.modalContainer) return;
    
    this.modalContainer.classList.remove('modal-hidden');
    this.modalContainer.classList.add('modal-visible');
    
    // Load Turnstile widget
    this.loadTurnstile();
  }

  close() {
    if (!this.modalContainer) return;
    this.modalContainer.classList.remove('modal-visible');
    this.modalContainer.classList.add('modal-hidden');
    
    // Reset form
    const form = this.modalContainer.querySelector('#upload-form') as HTMLFormElement;
    form?.reset();
    
    const previewContainer = this.modalContainer.querySelector('#preview-container') as HTMLElement;
    const modalContent = this.modalContainer.querySelector('.upload-modal-content') as HTMLElement;
    const uploadFormFields = this.modalContainer?.querySelector('.upload-form-fields') as HTMLElement;
    
    if (previewContainer) {
      previewContainer.classList.remove('visible');
      uploadFormFields?.classList.remove('has-preview');
      previewContainer.style.display = 'none';
    }
    if (modalContent) {
      modalContent.classList.remove('has-preview');
    }
    
    this.formData = {
      username: '',
      description: '',
      file: null,
      turnstileToken: ''
    };
  }

  handleFileSelect() {
    const fileInput = this.modalContainer?.querySelector('#file-input') as HTMLInputElement;
    const preview = this.modalContainer?.querySelector('#image-preview') as HTMLImageElement;
    const previewContainer = this.modalContainer?.querySelector('#preview-container') as HTMLElement;
    const modalContent = this.modalContainer?.querySelector('.upload-modal-content') as HTMLElement;
    const uploadFormFields = this.modalContainer?.querySelector('.upload-form-fields') as HTMLElement;
    
    if (fileInput.files && fileInput.files[0]) {
      const file = fileInput.files[0];
      this.formData.file = file;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        if (preview && e.target?.result) {
          preview.src = e.target.result as string;
          if (previewContainer) {
            previewContainer.classList.add('visible');
            uploadFormFields?.classList.remove('has-preview');
          }
          if (modalContent) {
            modalContent.classList.add('has-preview');
          }
        }
      };
      reader.readAsDataURL(file);
    }
  }

  async handleSubmit(e: Event) {
    e.preventDefault();
    
    if (!this.modalContainer) return;
    
    const form = this.modalContainer.querySelector('#upload-form') as HTMLFormElement;
    const usernameInput = form.querySelector('#username') as HTMLInputElement;
    const descriptionInput = form.querySelector('#description') as HTMLTextAreaElement;
    
    this.formData.username = usernameInput.value;
    this.formData.description = descriptionInput.value;
    
    // Get Turnstile token
    if ((window as any).turnstile && this.turnstileWidgetId !== null) {
      this.formData.turnstileToken = (window as any).turnstile.getResponse(this.turnstileWidgetId);
    }
    
    if (!this.formData.turnstileToken) {
      alert('Please complete the captcha verification');
      return;
    }
    
    if (!this.formData.file) {
      alert('Please select an image to upload');
      return;
    }
    
    // Show loading spinner
    const loadingOverlay = this.modalContainer.querySelector('.loading-overlay') as HTMLElement;
    if (loadingOverlay) loadingOverlay.style.display = 'flex';
    
    try {
      await this.uploadImage();
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message || error}`);
    } finally {
      if (loadingOverlay) loadingOverlay.style.display = 'none';
    }
  }

  async uploadImage() {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
    
    const formData = new FormData();
    formData.append('username', this.formData.username);
    formData.append('description', this.formData.description);
    if (this.formData.file) {
      formData.append('file', this.formData.file);
    }
    formData.append('cf-turnstile-response', this.formData.turnstileToken);
    
    const response = await fetch(`${apiUrl}/images`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }
    
    const result = await response.json();
    
    // Close modal
    this.close();
    
    // Call success callback with image data
    if (this.onUploadSuccess && result.image) {
      this.onUploadSuccess(result.image);
    }
  }

  loadTurnstile() {
    // Wait for Turnstile to be available
    const checkTurnstile = () => {
      if ((window as any).turnstile) {
        const container = this.modalContainer?.querySelector('#captcha-container');
        if (container && this.turnstileWidgetId === null) {
          this.turnstileWidgetId = (window as any).turnstile.render(container, {
            sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA', // Test key
            theme: 'light',
          });
        }
      } else {
        setTimeout(checkTurnstile, 100);
      }
    };
    checkTurnstile();
  }

  dispose() {
    if (this.modalContainer) {
      document.body.removeChild(this.modalContainer);
      this.modalContainer = null;
    }
  }
}
